import {
  useCallback,
} from "react";
import { useInterfaceBuilderAsyncRunner } from "@/components/Layout/account/useInterfaceBuilderAsyncRunner";
import {
  createUiComponent,
  createUiComponents,
  createUiSidebarItem,
  deleteUiComponents,
  updateUiComponent,
  deleteUiSidebarItem,
} from "@/services/uiApi";
import {
  collectComponentDeleteOrder,
  collectComponentSubtree,
  createEmbeddedComponentScheme,
  moveComponentInAncestorSchemes,
  readSchemeChildren,
  readSchemeNodePosition,
  removeComponentsFromAncestorSchemes,
  schemeReferencesComponent,
  updateComponentSchemeChildren,
  upsertComponentAndAncestorSchemes,
} from "@/interfaceBuilder/componentSchemeTree";
import {
  removeUiComponents,
  upsertUiComponent,
  upsertUiComponents,
  type StoredUiComponent,
} from "@/storage/uiComponentsRepository";
import {
  removeUiSidebarItem,
  removeUiSidebarItems,
  upsertUiSidebarItem,
  type StoredUiSidebarItem,
} from "@/storage/uiSidebarItemsRepository";
import {
  remapComponentReferences,
} from "@/interfaceBuilder/componentReferenceRemap";
import { requestProjectAccessToken } from "@/services/authRefreshSession";
import { createUiStorageScope } from "@/storage/uiStorageScope";
import { createBearerAuthHeaders } from "@/services/httpHeaders";
import type {
  ImportedManifestComponent,
  ImportedSidebarItemManifest,
} from "@/interfaceBuilder/sidebarItemManifest";
import {
  normalizeUiComponentId,
} from "@/utils/uiComponentLinks";
import type { UiComponent } from "@/types/ui";

interface InterfaceBuilderSidebarActionParams {
  components: readonly StoredUiComponent[];
  isAuthenticated: boolean;
  userId?: string | null;
  projectId?: string | null;
  sidebarItems: readonly StoredUiSidebarItem[];
}

interface ActionCallbacks {
  onComponentOpened?: (componentId: string) => void;
  onComponentRemoved?: (componentId: string) => void;
  onSidebarItemOpened?: (sidebarItemId: string) => void;
  onSidebarItemRemoved?: (item: StoredUiSidebarItem) => void;
}

interface ComponentMovePlan {
  position: number;
  repositionedSiblings: readonly {
    component: StoredUiComponent;
    position: number;
  }[];
}

interface CreateComponentOptions {
  kind?: string;
  parentUuid?: string | null;
}

// A group is a plain layout container, so grouping never changes how the
// grouped components look; it only adds a level to the tree.
const GROUP_COMPONENT_KIND = "view";

// A manifest states the kind of every component it declares; one that leaves it
// out is read as a plain layout container.
const DEFAULT_IMPORTED_COMPONENT_KIND = "view";

/**
 * A group replaces its members in one parent's child list, so every component
 * being grouped has to be a sibling of the others.
 */
export function canGroupComponents(
  componentsToGroup: readonly StoredUiComponent[],
): boolean {
  if (componentsToGroup.length < 2) return false;

  const parentUuid = componentsToGroup[0].parentUuid;
  return componentsToGroup.every(
    (component) => component.parentUuid === parentUuid,
  );
}

export function useInterfaceBuilderSidebarActions({
  components,
  isAuthenticated,
  projectId,
  sidebarItems,
  userId,
}: InterfaceBuilderSidebarActionParams) {
  const {
    clearError: clearActionError,
    error: actionError,
    errorDetails: actionErrorDetails,
    run: runSidebarAction,
    submitting,
  } = useInterfaceBuilderAsyncRunner({
    errorFallbackKey: "interfaceBuilderSidebarMenu.actionError",
    isAuthenticated,
    projectId,
    userId,
  });

  const createComponent = useCallback(
    async (
      { onComponentOpened }: ActionCallbacks = {},
      { kind, parentUuid }: CreateComponentOptions = {},
    ) => runSidebarAction(async (signal, scopeKey) => {
      // Only the kind and parent are declared here; the server generates the
      // name and fills every other property from its model defaults.
      const component = await createUiComponent({
        ...(kind ? { kind } : {}),
        ...(parentUuid !== undefined ? { parentUuid } : {}),
      }, { signal });
      await upsertUiComponent(scopeKey, component);
      if (parentUuid) {
        await attachChildToParent(scopeKey, components, parentUuid, component);
      }
      onComponentOpened?.(component.uuid);
    }),
    [components, runSidebarAction],
  );

  const cloneComponent = useCallback(
    async (component: StoredUiComponent) => runSidebarAction(async (signal, scopeKey) => {
      const parentUuid = component.parentUuid ?? null;
      const usedNames = collectUsedNames(components, sidebarItems);
      // Depth-first from the cloned component, so every clone is described
      // after the parent it is attached to.
      const subtree = collectComponentSubtree(components, component);
      const clonesByOriginalUuid = new Map<string, UiComponent>();

      for (const original of subtree) {
        const isRoot = original.uuid === component.uuid;
        const clonedParentUuid = isRoot
          ? parentUuid
          : clonesByOriginalUuid.get(original.parentUuid ?? "")?.uuid ?? null;
        const name = createUniqueComponentName(
          isRoot ? `${original.name} copy` : original.name,
          usedNames,
        );

        clonesByOriginalUuid.set(
          original.uuid,
          createClonedComponent(original, name, clonedParentUuid, projectId),
        );
      }

      // A property of the original may address another component of the same
      // subtree — a table naming its data source, a form field mirroring an
      // input. Once every clone has a uuid, those properties are rewritten to
      // address the clones, so the copied subtree stands on its own instead of
      // feeding off the originals. References out of the subtree are left as
      // they are: those components are shared, not copied.
      const clonedUuidsByOriginalUuid = new Map(
        [...clonesByOriginalUuid].map(([originalUuid, clone]) => [
          originalUuid,
          clone.uuid,
        ]),
      );
      for (const [originalUuid, clone] of clonesByOriginalUuid) {
        clonesByOriginalUuid.set(
          originalUuid,
          remapClonedComponentReferences(clone, clonedUuidsByOriginalUuid),
        );
      }

      // The whole subtree in one request: naming the clones here is what lets
      // a child be written alongside the parent it links to, instead of the
      // tree costing a round trip per component.
      const clones = [...clonesByOriginalUuid.values()];
      await createUiComponents(
        clones.map((clone) => ({
          uuid: clone.uuid,
          parentUuid: clone.parentUuid,
          props: clone.uiScheme,
        })),
        { signal },
      );

      // Children are embedded in their parent's scheme, so the clones are filled
      // in bottom-up: every clone embeds children that already carry their own.
      for (const original of [...subtree].reverse()) {
        const clone = clonesByOriginalUuid.get(original.uuid);
        if (!clone) continue;

        const clonedChildren = subtree
          .filter((candidate) => candidate.parentUuid === original.uuid)
          .flatMap((child) => {
            const childClone = clonesByOriginalUuid.get(child.uuid);
            return childClone ? [createEmbeddedComponentScheme(childClone)] : [];
          });
        const clonedComponent = updateComponentSchemeChildren(
          clone,
          () => clonedChildren,
        );

        clonesByOriginalUuid.set(original.uuid, clonedComponent);
      }

      // The subtree is stored in one go: a write per component would announce
      // the store that many times, and the tree and the canvas are drawn again
      // on every announcement.
      await upsertUiComponents(scopeKey, [...clonesByOriginalUuid.values()]);

      const rootClone = clonesByOriginalUuid.get(component.uuid);
      if (rootClone && parentUuid) {
        await attachChildToParent(scopeKey, components, parentUuid, rootClone);
      }
    }),
    [components, projectId, runSidebarAction, sidebarItems],
  );

  /**
   * Writes a copy of the component and everything under it into another
   * project.
   *
   * The copy is a page root there: the parent it hangs from belongs to the
   * project it is leaving, and the project it lands in names its own tree.
   *
   * The server decides which project a component is written into by the token
   * the request carries -- `project_id` is read-only on the resource -- so the
   * batch carries a token of the target project. The session stays signed into
   * its own project throughout: moving it over and back would point every other
   * request at the wrong project while the write ran, and strand it there if
   * the way back failed. A copy into the project the session is already signed
   * into needs no token of its own.
   *
   * Which project the component is leaving is the component's own, not the
   * session's: the pages of the administration project are read by every
   * account, and copying one of those into the visitor's project is the very
   * thing this is for.
   */
  const copyComponentToProject = useCallback(
    async (component: StoredUiComponent, targetProjectId: string) => (
      runSidebarAction(async (signal) => {
        const sourceProjectId = component.projectId;
        if (!sourceProjectId || targetProjectId === sourceProjectId) return false;

        // Depth-first from the component, so a child is described after the
        // parent it links to.
        const subtree = collectComponentSubtree(components, component);
        const copiesByOriginalUuid = new Map<string, UiComponent>();
        // A copy landing in the project the builder is showing stands beside
        // the component it was made from, so it is named the way a clone is.
        // Another project names its own tree, and the builder has not read it.
        const usedNames = targetProjectId === projectId
          ? collectUsedNames(components, sidebarItems)
          : null;

        for (const original of subtree) {
          const isRoot = original.uuid === component.uuid;
          copiesByOriginalUuid.set(
            original.uuid,
            createClonedComponent(
              original,
              usedNames
                ? createUniqueComponentName(
                  isRoot ? `${original.name} copy` : original.name,
                  usedNames,
                )
                : original.name,
              isRoot
                ? null
                : copiesByOriginalUuid.get(original.parentUuid ?? "")?.uuid
                  ?? null,
              targetProjectId,
            ),
          );
        }

        // A property may address another component of the same subtree, and
        // that component is being copied too, so the copies address each other
        // rather than the originals they were made from. References out of the
        // subtree are left alone: they name components of the project the copy
        // is leaving, which is as much as this action knows about them.
        const copiedUuidsByOriginalUuid = new Map(
          [...copiesByOriginalUuid].map(([originalUuid, copy]) => [
            originalUuid,
            copy.uuid,
          ]),
        );
        for (const [originalUuid, copy] of copiesByOriginalUuid) {
          copiesByOriginalUuid.set(
            originalUuid,
            remapClonedComponentReferences(copy, copiedUuidsByOriginalUuid),
          );
        }

        let headers: Record<string, string> | undefined;
        if (targetProjectId !== projectId) {
          const accessToken = await requestProjectAccessToken(targetProjectId);
          if (!accessToken) return false;

          headers = createBearerAuthHeaders(accessToken);
        }

        // The whole subtree in one request, the way a clone is written.
        await createUiComponents(
          [...copiesByOriginalUuid.values()].map((copy) => ({
            uuid: copy.uuid,
            parentUuid: copy.parentUuid,
            props: copy.uiScheme,
          })),
          { signal, ...(headers ? { headers } : {}) },
        );

        // Children are embedded in their parent's scheme, so the copies are
        // filled in bottom-up, the way a clone is.
        for (const original of [...subtree].reverse()) {
          const copy = copiesByOriginalUuid.get(original.uuid);
          if (!copy) continue;

          copiesByOriginalUuid.set(original.uuid, updateComponentSchemeChildren(
            copy,
            () => subtree
              .filter((candidate) => candidate.parentUuid === original.uuid)
              .flatMap((child) => {
                const childCopy = copiesByOriginalUuid.get(child.uuid);
                return childCopy
                  ? [createEmbeddedComponentScheme(childCopy)]
                  : [];
              }),
          ));
        }

        // Stored under the project the copy landed in rather than the one it
        // left, so the visitor switching over there reads it at once instead of
        // waiting on the sync that scope runs when it is opened.
        const targetScope = createUiStorageScope({ projectId: targetProjectId, userId });
        if (targetScope) {
          await upsertUiComponents(
            targetScope.key,
            [...copiesByOriginalUuid.values()],
          );
        }
      })
    ),
    [components, projectId, runSidebarAction],
  );

  const createSidebarItem = useCallback(
    async (
      defaultName: string,
      { onSidebarItemOpened }: ActionCallbacks = {},
    ) => runSidebarAction(async (signal, scopeKey) => {
      const name = createUniqueComponentName(
        defaultName,
        collectUsedNames(components, sidebarItems),
      );
      const sidebarItem = await createUiSidebarItem({
        name,
        description: "",
        position: getNextSidebarPosition(sidebarItems),
      }, { signal });

      await upsertUiSidebarItem(scopeKey, sidebarItem);
      onSidebarItemOpened?.(sidebarItem.uuid);
    }),
    [components, runSidebarAction, sidebarItems],
  );

  const cloneSidebarItem = useCallback(
    async (
      item: StoredUiSidebarItem,
      { onSidebarItemOpened }: ActionCallbacks = {},
    ) => runSidebarAction(async (signal, scopeKey) => {
      const name = createUniqueComponentName(
        `${item.name} copy`,
        collectUsedNames(components, sidebarItems),
      );
      // The copy carries everything the original does — its icon, group,
      // permissions and translations — and links the same component; only the
      // name and the place in the menu are its own.
      const sidebarItem = await createUiSidebarItem({
        name,
        description: item.description,
        position: getNextSidebarPosition(sidebarItems),
        componentUuid: item.componentUuid,
        ...(item.props ? { props: item.props } : {}),
      }, { signal });

      await upsertUiSidebarItem(scopeKey, sidebarItem);
      onSidebarItemOpened?.(sidebarItem.uuid);
    }),
    [components, runSidebarAction, sidebarItems],
  );

  const importSidebarItem = useCallback(
    async (
      manifest: ImportedSidebarItemManifest,
      { onSidebarItemOpened }: ActionCallbacks = {},
    ) => runSidebarAction(async (signal, scopeKey) => {
      const usedNames = collectUsedNames(components, sidebarItems);
      // The manifest carries no uuids -- it addresses its components by the key
      // each is declared under -- so the builder names them here, and the keys
      // are what the parent links are resolved through. A parent is declared
      // above the children that link to it, so its uuid is already known by the
      // time they are read.
      const uuidsByKey = new Map<string, string>();
      const importedByKey = new Map<string, UiComponent>();

      for (const component of manifest.components) {
        const uuid = crypto.randomUUID();
        uuidsByKey.set(component.key, uuid);
        importedByKey.set(component.key, createImportedComponent(
          component,
          uuid,
          uuidsByKey.get(component.parentKey ?? "") ?? null,
          createUniqueComponentName(
            readImportedComponentName(component),
            usedNames,
          ),
          projectId,
        ));
      }

      // The whole page in one request, the way a cloned subtree is written.
      const imported = [...importedByKey.values()];
      await createUiComponents(
        imported.map((component) => ({
          uuid: component.uuid,
          parentUuid: component.parentUuid,
          props: component.uiScheme,
        })),
        { signal },
      );

      // Children are embedded in their parent's scheme, so the tree is filled
      // in bottom-up: the manifest declares parents above their children.
      for (const component of [...manifest.components].reverse()) {
        const importedComponent = importedByKey.get(component.key);
        if (!importedComponent) continue;

        const children = manifest.components.flatMap((candidate) => {
          if (candidate.parentKey !== component.key) return [];

          const child = importedByKey.get(candidate.key);
          return child ? [createEmbeddedComponentScheme(child)] : [];
        });
        const componentWithChildren = updateComponentSchemeChildren(
          importedComponent,
          () => children,
        );

        importedByKey.set(component.key, componentWithChildren);
      }

      // The whole page in one store write, the way a cloned subtree is stored.
      await upsertUiComponents(scopeKey, [...importedByKey.values()]);

      const rootComponentUuid = manifest.sidebarItem.componentKey
        ? uuidsByKey.get(manifest.sidebarItem.componentKey) ?? null
        : null;
      const sidebarItem = await createUiSidebarItem({
        name: createUniqueComponentName(manifest.sidebarItem.name, usedNames),
        description: manifest.sidebarItem.description,
        position: getNextSidebarPosition(sidebarItems),
        componentUuid: rootComponentUuid,
        props: manifest.sidebarItem.props,
      }, { signal });

      await upsertUiSidebarItem(scopeKey, sidebarItem);
      onSidebarItemOpened?.(sidebarItem.uuid);
    }),
    [components, projectId, runSidebarAction, sidebarItems],
  );

  const moveComponent = useCallback(
    async (
      component: StoredUiComponent,
      parentUuid: string | null,
      // Where the component lands among its new siblings: the uuid of the
      // sibling it goes in front of, or null to append. Omitted (undefined) —
      // the tree drop — only reparents and leaves the sibling order alone.
      beforeUuid?: string | null,
    ) => {
      if (
        parentUuid === component.uuid
        || beforeUuid === component.uuid
        || isComponentDescendant(components, component.uuid, parentUuid)
      ) {
        return false;
      }

      const movePlan = beforeUuid === undefined
        ? null
        : createComponentMovePlan(components, component, parentUuid, beforeUuid);
      if (
        beforeUuid === undefined
          ? component.parentUuid === parentUuid
          : movePlan === null
      ) {
        return false;
      }

      return runSidebarAction(async (signal, scopeKey) => {
        const savedComponent = await updateUiComponent(
          component.uuid,
          {
            parentUuid,
            props: {
              ...createComponentUpdateProps(component),
              ...(movePlan ? { position: movePlan.position } : {}),
            },
          },
          { signal },
        );
        const movedComponent = {
          ...component,
          ...savedComponent,
          parentUuid,
          uiScheme: {
            ...component.uiScheme,
            ...savedComponent.uiScheme,
            children: readComponentChildren(component),
          },
        };
        const repositionedComponents: StoredUiComponent[] = [];
        for (const sibling of movePlan?.repositionedSiblings ?? []) {
          const savedSibling = await updateUiComponent(
            sibling.component.uuid,
            {
              parentUuid: sibling.component.parentUuid,
              props: {
                ...createComponentUpdateProps(sibling.component),
                position: sibling.position,
              },
            },
            { signal },
          );
          repositionedComponents.push({
            ...sibling.component,
            ...savedSibling,
            parentUuid: sibling.component.parentUuid,
            uiScheme: {
              ...sibling.component.uiScheme,
              ...savedSibling.uiScheme,
              children: readComponentChildren(sibling.component),
            },
          });
        }

        await moveComponentInAncestorSchemes(
          scopeKey,
          components,
          movedComponent,
          component.parentUuid,
          repositionedComponents,
        );
      });
    },
    [components, runSidebarAction],
  );

  const resizeComponent = useCallback(
    async (
      component: StoredUiComponent,
      size: { height: number; width: number },
    ) => {
      const currentSize = readComponentSize(component);
      if (
        currentSize.height === size.height
        && currentSize.width === size.width
      ) {
        return false;
      }

      return runSidebarAction(async (signal, scopeKey) => {
        const savedComponent = await updateUiComponent(
          component.uuid,
          {
            parentUuid: component.parentUuid,
            props: {
              ...createComponentUpdateProps(component),
              height: size.height,
              width: size.width,
            },
          },
          { signal },
        );

        await upsertComponentAndAncestorSchemes(scopeKey, components, {
          ...component,
          ...savedComponent,
          parentUuid: component.parentUuid,
          uiScheme: {
            ...component.uiScheme,
            ...savedComponent.uiScheme,
            children: readComponentChildren(component),
          },
        });
      });
    },
    [components, runSidebarAction],
  );

  // Editing a label's text on the canvas saves it as the component's `value`.
  const editComponentText = useCallback(
    async (component: StoredUiComponent, value: string) => {
      const scheme = component.uiScheme as Record<string, unknown>;
      if (scheme.value === value) return false;

      return runSidebarAction(async (signal, scopeKey) => {
        const savedComponent = await updateUiComponent(
          component.uuid,
          {
            parentUuid: component.parentUuid,
            props: {
              ...createComponentUpdateProps(component),
              value,
            },
          },
          { signal },
        );

        await upsertComponentAndAncestorSchemes(scopeKey, components, {
          ...component,
          ...savedComponent,
          parentUuid: component.parentUuid,
          uiScheme: {
            ...component.uiScheme,
            ...savedComponent.uiScheme,
            children: readComponentChildren(component),
          },
        });
      });
    },
    [components, runSidebarAction],
  );

  const groupComponents = useCallback(
    async (componentsToGroup: readonly StoredUiComponent[]) => {
      if (!canGroupComponents(componentsToGroup)) return false;

      const parentUuid = componentsToGroup[0].parentUuid;

      return runSidebarAction(async (signal, scopeKey) => {
        const createdGroup = await createUiComponent({
          kind: GROUP_COMPONENT_KIND,
          parentUuid,
        }, { signal });
        // The group takes the topmost position of its members so it keeps their
        // place among the siblings that stay behind. The parent is resent because
        // the update replaces the component's props.
        const savedGroup = await updateUiComponent(createdGroup.uuid, {
          parentUuid,
          props: {
            ...createComponentUpdateProps(createdGroup),
            position: Math.min(...componentsToGroup.map(
              (component) => readSchemeNodePosition(component.uiScheme),
            )),
          },
        }, { signal });

        const movedComponents: UiComponent[] = [];
        for (const component of componentsToGroup) {
          const savedComponent = await updateUiComponent(component.uuid, {
            parentUuid: savedGroup.uuid,
            props: createComponentUpdateProps(component),
          }, { signal });
          const movedComponent = {
            ...component,
            ...savedComponent,
            parentUuid: savedGroup.uuid,
            uiScheme: {
              ...component.uiScheme,
              ...savedComponent.uiScheme,
              children: readComponentChildren(component),
            },
          };

          await upsertUiComponent(scopeKey, movedComponent);
          movedComponents.push(movedComponent);
        }

        const group = updateComponentSchemeChildren(savedGroup, () => (
          movedComponents.map(createEmbeddedComponentScheme)
        ));
        await upsertUiComponent(scopeKey, group);
        await replaceGroupedChildrenInParent(
          scopeKey,
          components,
          componentsToGroup,
          group,
        );
      });
    },
    [components, runSidebarAction],
  );

  const hideSidebarItem = useCallback(
    async (
      item: StoredUiSidebarItem,
      { onSidebarItemRemoved }: ActionCallbacks = {},
    ) => runSidebarAction(async (signal, scopeKey) => {
      await deleteUiSidebarItem(item.uuid, { signal });
      await removeUiSidebarItem(scopeKey, item.uuid);
      onSidebarItemRemoved?.(item);
    }),
    [runSidebarAction],
  );

  const deleteComponents = useCallback(
    async (
      componentsToRemove: readonly StoredUiComponent[],
      { onComponentRemoved }: ActionCallbacks = {},
    ) => runSidebarAction(async (signal, scopeKey) => {
      // Children before parents, and each record once: a selection may hold both
      // a component and something nested inside it.
      const componentIdsToDelete = new Set<string>();
      const componentsToDelete: StoredUiComponent[] = [];
      componentsToRemove.forEach((component) => {
        collectComponentDeleteOrder(components, component).forEach(
          (currentComponent) => {
            if (componentIdsToDelete.has(currentComponent.uuid)) return;

            componentIdsToDelete.add(currentComponent.uuid);
            componentsToDelete.push(currentComponent);
          },
        );
      });
      const linkedSidebarItems = sidebarItems.filter((item) => {
        const linkedComponentId = normalizeUiComponentId(
          item.componentUuid || item.component?.uuid,
        );

        return linkedComponentId
          ? componentIdsToDelete.has(linkedComponentId)
          : false;
      });

      for (const item of linkedSidebarItems) {
        await deleteUiSidebarItem(item.uuid, { signal });
      }
      // The whole selection in one request, and one transaction: a component
      // the server cannot remove leaves the rest of the tree standing rather
      // than a page half of which is gone.
      await deleteUiComponents(
        componentsToDelete.map((currentComponent) => currentComponent.uuid),
        { signal },
      );

      // One write each, rather than one per record: the tree and the canvas are
      // drawn again on every announcement the store makes.
      await Promise.all([
        removeUiSidebarItems(
          scopeKey,
          linkedSidebarItems.map((item) => item.uuid),
        ),
        removeUiComponents(
          scopeKey,
          componentsToDelete.map((currentComponent) => currentComponent.uuid),
        ),
      ]);
      // A parent that is being deleted itself has no scheme left to rewrite.
      await removeComponentsFromAncestorSchemes(
        scopeKey,
        components,
        componentsToRemove.filter((component) => (
          component.parentUuid !== null
          && !componentIdsToDelete.has(component.parentUuid)
        )),
      );
      componentsToDelete.forEach((currentComponent) => {
        onComponentRemoved?.(currentComponent.uuid);
      });
    }),
    [components, runSidebarAction, sidebarItems],
  );

  return {
    actionError,
    actionErrorDetails,
    clearActionError,
    cloneComponent,
    cloneSidebarItem,
    copyComponentToProject,
    createComponent,
    editComponentText,
    importSidebarItem,
  createSidebarItem,
  groupComponents,
  moveComponent,
  resizeComponent,
  deleteComponents,
    hideSidebarItem,
    submitting,
  };
}

/**
 * The component a manifest resource is stored as. Its parent is the component
 * the manifest links it to -- a link the file states by key -- and its props
 * are the ones the batch writes, so the stored component says exactly what
 * storage holds.
 */
function createImportedComponent(
  component: ImportedManifestComponent,
  uuid: string,
  parentUuid: string | null,
  name: string,
  projectId?: string | null,
): UiComponent {
  const kind = typeof component.props.kind === "string"
    ? component.props.kind
    : DEFAULT_IMPORTED_COMPONENT_KIND;
  // The identity is the record's own, the way a clone stores it: the scheme
  // carries the properties, and the children are embedded once the whole tree
  // is named.
  const uiScheme = {
    ...component.props,
    kind,
    name,
    parent_uuid: parentUuid,
    children: [],
  };

  return {
    uuid,
    name,
    description: typeof component.props.description === "string"
      ? component.props.description
      : "",
    projectId: projectId ?? null,
    parentUuid,
    componentType: kind,
    props: uiScheme,
    uiScheme,
  };
}

function readImportedComponentName(
  component: ImportedManifestComponent,
): string {
  const name = typeof component.props.name === "string"
    ? component.props.name.trim()
    : "";

  return name || component.key;
}

async function attachChildToParent(
  scopeKey: string,
  components: readonly StoredUiComponent[],
  parentUuid: string,
  child: UiComponent,
): Promise<void> {
  const parent = components.find((component) => component.uuid === parentUuid);
  if (!parent) return;

  await upsertComponentAndAncestorSchemes(
    scopeKey,
    components,
    updateComponentSchemeChildren(parent, (children) => [
      ...children,
      createEmbeddedComponentScheme(child),
    ]),
  );
}

/**
 * Swaps the grouped components for the group itself inside their shared
 * parent's embedded scheme, then propagates the parent up its own ancestors.
 * Top-level components have no parent scheme to rewrite.
 */
async function replaceGroupedChildrenInParent(
  scopeKey: string,
  components: readonly StoredUiComponent[],
  groupedComponents: readonly StoredUiComponent[],
  group: UiComponent,
): Promise<void> {
  const parentUuid = group.parentUuid;
  if (!parentUuid) return;

  const parent = components.find((component) => component.uuid === parentUuid);
  if (!parent) return;

  await upsertComponentAndAncestorSchemes(
    scopeKey,
    components,
    updateComponentSchemeChildren(parent, (children) => [
      ...children.filter((scheme) => !groupedComponents.some(
        (component) => schemeReferencesComponent(scheme, component),
      )),
      createEmbeddedComponentScheme(group),
    ]),
  );
}

/**
 * Works out the sibling order a drop produces. `position` is renumbered from 0
 * over the whole target parent, because the rendered order sorts by it, so the
 * siblings the drop shifts have to be renumbered too. Returns null when the
 * component would land exactly where it already is.
 */
function createComponentMovePlan(
  components: readonly StoredUiComponent[],
  component: StoredUiComponent,
  parentUuid: string | null,
  beforeUuid: string | null,
): ComponentMovePlan | null {
  const currentSiblings = sortSiblingComponents(components.filter(
    (sibling) => (sibling.parentUuid ?? null) === parentUuid,
  ));
  const siblings = currentSiblings.filter(
    (sibling) => sibling.uuid !== component.uuid,
  );
  const beforeIndex = beforeUuid
    ? siblings.findIndex((sibling) => sibling.uuid === beforeUuid)
    : -1;
  const nextSiblings = beforeIndex < 0
    ? [...siblings, component]
    : [
      ...siblings.slice(0, beforeIndex),
      component,
      ...siblings.slice(beforeIndex),
    ];
  const unchanged = component.parentUuid === parentUuid
    && currentSiblings.length === nextSiblings.length
    && currentSiblings.every(
      (sibling, index) => sibling.uuid === nextSiblings[index].uuid,
    );
  if (unchanged) return null;

  return {
    position: nextSiblings.findIndex(
      (sibling) => sibling.uuid === component.uuid,
    ),
    repositionedSiblings: nextSiblings.flatMap((sibling, index) => (
      sibling.uuid !== component.uuid
        && readSchemeNodePosition(sibling.uiScheme) !== index
        ? [{ component: sibling, position: index }]
        : []
    )),
  };
}

// Mirrors how the API layer orders embedded children, so the index a drop
// computes on the canvas addresses the same sibling here.
function sortSiblingComponents(
  siblings: readonly StoredUiComponent[],
): StoredUiComponent[] {
  return [...siblings].sort((a, b) => (
    readSchemeNodePosition(a.uiScheme) - readSchemeNodePosition(b.uiScheme)
    || a.name.localeCompare(b.name)
    || a.uuid.localeCompare(b.uuid)
  ));
}

function isComponentDescendant(
  components: readonly StoredUiComponent[],
  ancestorUuid: string,
  componentUuid: string | null,
): boolean {
  if (!componentUuid) return false;

  const componentsByUuid = new Map(
    components.map((component) => [component.uuid, component]),
  );
  let currentUuid: string | null = componentUuid;
  const visited = new Set<string>();
  while (currentUuid && !visited.has(currentUuid)) {
    if (currentUuid === ancestorUuid) return true;
    visited.add(currentUuid);
    currentUuid = componentsByUuid.get(currentUuid)?.parentUuid ?? null;
  }

  return false;
}

function readComponentSize(
  component: UiComponent,
): { height: number | null; width: number | null } {
  const scheme = component.uiScheme as Record<string, unknown>;

  return {
    height: readOptionalNumber(scheme.height),
    width: readOptionalNumber(scheme.width),
  };
}

function readOptionalNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;

  const parsed = typeof value === "string" ? Number(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : null;
}

function readComponentChildren(component: UiComponent): readonly unknown[] {
  return readSchemeChildren(component.uiScheme);
}

function readComponentKind(component: UiComponent): string {
  const kind = component.uiScheme.kind;

  return typeof kind === "string" ? kind : component.componentType;
}

function createComponentUpdateProps(
  component: UiComponent,
): Record<string, unknown> {
  const props = createClonedComponentProps(component.uiScheme, component.name);
  props.kind = readComponentKind(component);
  return props;
}

/**
 * The component a clone is stored as, named here rather than by the server.
 *
 * Everything but the identity is the original's: the clone is the same kind,
 * so it carries the same property spec, and its properties are the ones the
 * batch writes, so the stored component says exactly what storage holds.
 */
function createClonedComponent(
  original: UiComponent,
  name: string,
  parentUuid: string | null,
  projectId?: string | null,
): UiComponent {
  const props = createClonedComponentProps(original.uiScheme, name);
  props.kind = readComponentKind(original);
  // The parent link is a property like any other, and an update replaces the
  // whole property set: a clone stored without it would be written back to the
  // server without a parent the first time one of its properties is edited, and
  // would surface at the top of the tree on the next sync.
  props.parent_uuid = parentUuid;
  // The children are embedded once every clone in the subtree is named.
  const uiScheme = { ...props, children: [] };

  return {
    ...original,
    uuid: crypto.randomUUID(),
    name,
    parentUuid,
    projectId: projectId ?? null,
    props: uiScheme,
    uiScheme,
    // Written by storage, and only read back on the next sync.
    createdAt: undefined,
    updatedAt: undefined,
  };
}

/**
 * The clone with every reference to a component of the same subtree pointing at
 * that component's clone. `props` and `uiScheme` describe the same properties,
 * so both carry the rewritten scheme.
 */
function remapClonedComponentReferences(
  clone: UiComponent,
  clonedUuidsByOriginalUuid: ReadonlyMap<string, string>,
): UiComponent {
  const uiScheme = remapComponentReferences(
    clone.uiScheme as Record<string, unknown>,
    clonedUuidsByOriginalUuid,
  );

  return { ...clone, props: uiScheme, uiScheme };
}

function createClonedComponentProps(
  scheme: UiComponent["uiScheme"],
  name: string,
): Record<string, unknown> {
  const {
    children: _children,
    parent_uuid: _parentUuid,
    uuid: _uuid,
    ...props
  } = scheme as Record<string, unknown>;

  return {
    ...props,
    name,
  };
}

function getNextSidebarPosition(
  sidebarItems: readonly StoredUiSidebarItem[],
): number {
  return sidebarItems.reduce(
    (maxPosition, item) => Math.max(maxPosition, item.position),
    -1,
  ) + 1;
}

function collectUsedNames(
  components: readonly StoredUiComponent[],
  sidebarItems: readonly StoredUiSidebarItem[],
): Set<string> {
  return new Set([
    ...components.map((component) => component.name),
    ...sidebarItems.map((item) => item.name),
  ]);
}

/**
 * Records the name it returns in `usedNames`, so one action naming several
 * components in a row (cloning a subtree) keeps them apart from each other.
 */
function createUniqueComponentName(
  defaultName: string,
  usedNames: Set<string>,
): string {
  let name = defaultName;
  let index = 2;
  while (usedNames.has(name)) {
    name = `${defaultName} ${index}`;
    index += 1;
  }

  usedNames.add(name);
  return name;
}
